import { useMemo, useState } from 'react';
import { P2GBox } from '@parcel2go/core-ui';
import { SmartSendFilter, SmartSendFilterContext, SortField } from '../../../lib/helpers/SmartSendFilterApplier';
import { P2GText } from '@parcel2go/core-ui';
import { P2GDivider } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GLinkGroup } from '@parcel2go/core-ui';
import DebouncedInput from '../../DebouncedInput';
import FilterItemView from './FilterItemView';
import { P2GAlert } from '@parcel2go/core-ui';
import { SmartSendItemsCache } from '../../../lib/cache/SmartSendItemsCache';
import GenerateFilterOptions from '../../../lib/helpers/GenerateFilterOptions';
import useProviderName from '../../../hooks/use-provider-name';
import { Tagged } from '../../Tagged';
import { PanelButtons } from '../panels/PanelButtons';
import { P2GSidePanel } from '~/components/sidepanel';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';

interface props {
  title: string;
  filterKey: string;
  filter: SmartSendFilterContext;
  open: boolean;
  isXs: boolean;
  count: number;
  close: () => void;
  update: (filter: SmartSendFilter) => void;
  actions: SortField[];
}

export function Nav(props: { onBack?: () => void; onClose?: () => void }) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <P2GBox pb={2}>
      <P2GBox mb={2}>
        <P2GLinkGroup
          links={[
            props.onBack && <P2GLink onClick={props.onBack}>{t('BACK_LINK', { defaultValue: 'Back' })}</P2GLink>,
            props.onClose && (
              <P2GLink onClick={props.onClose}>
                {t('CLOSE_FILTER_LINK', { defaultValue: 'Close Filters / Search' })}
              </P2GLink>
            )
          ]}
        />
      </P2GBox>
      <P2GDivider />
    </P2GBox>
  );
}

export default function SmartSendFilterSideBar(props: props) {
  const [showFilter, setShowFilter] = useState(false);
  const [changes, setChanges] = useState(false);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const providerName = useProviderName();

  const availableFilters = useMemo(() => {
    return props.open
      ? GenerateFilterOptions(SmartSendItemsCache, props.filter.AvailableFilters, providerName).filter(
          (f) => !f.hidden && f.options.length > 1
        )
      : [];
  }, [props.open, providerName]);

  const alwaysShow = props.isXs ? ['MarketplaceSource'] : ['Tag', 'MarketplaceSource', 'Account'];

  const togglableFilters = availableFilters.filter((f) => !alwaysShow.includes(f.name));
  const openAlwaysFilters = availableFilters.filter((f) => alwaysShow.includes(f.name));

  return (
    <P2GSidePanel anchor="right" open={props.open} onClose={props.close} title={props.title} width="750px">
      <P2GBox onClick={() => setChanges(true)}>
        <Tagged tag="panel-filter">
          <div style={{ maxWidth: '100%' }}>
            <P2GGrid container direction="column" spacing={2}>
              <P2GGrid item>
                <DebouncedInput
                  value={props.filter.CurrentFilter.SearchTerm}
                  onChange={(value, _isValid) => props.update({ ...props.filter.CurrentFilter, SearchTerm: value })}
                  id="Search"
                  label={t('SEARCH_INPUT_LABEL', { defaultValue: 'Search' })}
                />
              </P2GGrid>
              <P2GGrid item>
                <P2GGrid container alignItems="center" spacing={2}>
                  <P2GGrid item>
                    <P2GText>
                      <Trans
                        t={t}
                        i18nKey="RESULTS_INTRO_TEXT"
                        values={{
                          count: props.count
                        }}
                        defaults="<strong>{{count}} Results</strong>"
                      />
                    </P2GText>
                  </P2GGrid>
                  {!props.isXs && (
                    <P2GGrid item>
                      <P2GLink variant="body2" onClick={() => setShowFilter(!showFilter)}>
                        {showFilter ? (
                          <>{t('HIDE', { defaultValue: 'Hide' })}</>
                        ) : (
                          <>{t('REFINE_RESULTS', { defaultValue: 'Refine Results / Show More Filters' })}</>
                        )}
                      </P2GLink>
                    </P2GGrid>
                  )}
                </P2GGrid>
              </P2GGrid>

              {props.count === 0 && (
                <P2GGrid item>
                  <P2GAlert color="info">
                    <P2GText>{t('NO_RESULTS_FALLBACK', { defaultValue: 'No Results...' })}</P2GText>
                  </P2GAlert>
                </P2GGrid>
              )}

              {(showFilter || openAlwaysFilters.length > 0) && (
                <P2GGrid item>
                  <P2GBox pt={2} pb={2}>
                    <P2GGrid container direction="column" gap={1}>
                      {showFilter &&
                        togglableFilters.map((f) => (
                          <P2GGrid item key={f.name}>
                            <P2GDivider style={{ margin: 0 }} />
                            <P2GBox pt={2} pb={2}>
                              <FilterItemView filter={props.filter} update={props.update} selected={f} />
                            </P2GBox>
                          </P2GGrid>
                        ))}
                      {openAlwaysFilters.map((f) => (
                        <P2GGrid item key={f.name}>
                          <P2GDivider style={{ margin: 0 }} />
                          <P2GBox pt={2} pb={2}>
                            <FilterItemView open filter={props.filter} update={props.update} selected={f} />
                          </P2GBox>
                        </P2GGrid>
                      ))}
                    </P2GGrid>
                  </P2GBox>
                </P2GGrid>
              )}
              <P2GGrid item>
                <PanelButtons
                  onSave={props.close}
                  saveText={t('VIEW_RESULTS_BUTTON', { defaultValue: 'View Results' })}
                  saveDisabled={!changes}
                />
              </P2GGrid>
            </P2GGrid>
          </div>
        </Tagged>
      </P2GBox>
    </P2GSidePanel>
  );
}
